import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../../App';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const ApiKeysCard = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showNewKey, setShowNewKey] = useState(false);

  const loadKeys = useCallback(async () => {
    if (user?.grade !== 'amiral') return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api-keys`);
      setKeys(data.keys || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
      toast.error('Erreur lors du chargement des clés API');
    } finally {
      setLoading(false);
    }
  }, [user?.grade]);

  useEffect(() => {
    if (isOpen && keys.length === 0) {
      loadKeys();
    }
  }, [isOpen, keys.length, loadKeys]);

  // Ne pas afficher si pas amiral
  if (user?.grade !== 'amiral') {
    return null;
  }

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Veuillez donner un nom à la clé');
      return;
    }

    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api-keys`, { name: newKeyName });
      setNewlyCreatedKey(data.key);
      setNewKeyName('');
      toast.success('Clé API créée avec succès');
      loadKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Erreur lors de la création de la clé');
    } finally {
      setCreating(false);
    }
  };

  const deleteKey = async (keyId) => {
    if (!window.confirm('Supprimer cette clé API ? Cette action est irréversible.')) return;

    try {
      await axios.delete(`${API}/api-keys/${keyId}`);
      toast.success('Clé supprimée');
      loadKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleKey = async (keyId, currentActive) => {
    try {
      await axios.patch(`${API}/api-keys/${keyId}`, { active: !currentActive });
      toast.success(currentActive ? 'Clé désactivée' : 'Clé activée');
      loadKeys();
    } catch (error) {
      console.error('Error toggling API key:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="api-keys-card">
      <CardHeader 
        className="cursor-pointer hover:bg-slate-800/30 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center justify-between text-purple-400">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Clés API (Import externe)
          </div>
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </CardTitle>
      </CardHeader>
      
      {isOpen && (
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-400">
            Gérez vos clés API pour automatiser l'import d'articles via des scripts externes.
          </p>

          {/* Nouvelle clé créée - affichage unique */}
          {newlyCreatedKey && (
            <div className="p-3 bg-emerald-900/30 border border-emerald-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-emerald-300 font-medium">Nouvelle clé créée</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewlyCreatedKey(null)}
                  className="h-6 w-6 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-800 px-2 py-1 rounded text-xs font-mono text-emerald-400 overflow-x-auto">
                  {showNewKey ? newlyCreatedKey : '••••••••••••••••••••••••••••••••'}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNewKey(!showNewKey)}
                  className="h-8 w-8 p-0"
                >
                  {showNewKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(newlyCreatedKey)}
                  className="h-8 w-8 p-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-amber-400 mt-2">
                ⚠️ Copiez cette clé maintenant, elle ne sera plus affichée.
              </p>
            </div>
          )}

          {/* Créer une nouvelle clé */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Nom de la clé (ex: Script import auto)"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
              data-testid="api-key-name-input"
            />
            <Button
              onClick={createKey}
              disabled={creating || !newKeyName.trim()}
              className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700"
              data-testid="create-api-key-btn"
            >
              <Plus className="w-4 h-4" />
              Créer
            </Button>
          </div>

          {/* Liste des clés */}
          {loading ? (
            <div className="text-slate-500 text-sm">Chargement...</div>
          ) : keys.length === 0 ? (
            <div className="text-slate-500 text-sm">Aucune clé API créée</div>
          ) : (
            <div className="space-y-2">
              {keys.map((key) => (
                <div 
                  key={key.id} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    key.active ? 'bg-slate-800/50' : 'bg-slate-800/20 opacity-60'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-200">{key.name}</span>
                      {key.active ? (
                        <span className="text-xs bg-emerald-600/30 text-emerald-400 px-2 py-0.5 rounded">Actif</span>
                      ) : (
                        <span className="text-xs bg-red-600/30 text-red-400 px-2 py-0.5 rounded">Inactif</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {key.key_preview} • Utilisée {key.usage_count || 0} fois
                      {key.last_used && ` • Dernière utilisation: ${new Date(key.last_used).toLocaleDateString('fr-FR')}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleKey(key.id, key.active)}
                      className="h-8 w-8 p-0"
                      title={key.active ? 'Désactiver' : 'Activer'}
                    >
                      {key.active ? <Check className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-red-400" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteKey(key.id)}
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Documentation API */}
          <div className="text-xs text-slate-500 space-y-2 bg-slate-800/50 p-3 rounded-lg">
            <p className="text-slate-400 font-medium">Utilisation de l'API :</p>
            <code className="block bg-slate-900 p-2 rounded text-[10px] font-mono overflow-x-auto whitespace-pre">
{`curl -X POST "https://votre-domaine/api/external/bulk-import/csv" \\
  -H "X-API-Key: votre-cle-api" \\
  -F "file=@articles.csv" \\
  -F "site=Armees.com" \\
  -F "status=publish"`}
            </code>
            <p className="mt-2"><strong>Paramètres :</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li><code>file</code> : Fichier CSV</li>
              <li><code>site</code> : Nom exact du site (ex: "Armees.com")</li>
              <li><code>status</code> : "draft" ou "publish"</li>
            </ul>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ApiKeysCard;
